import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSnackbar } from "notistack";
import "react-phone-input-2/lib/style.css";

import { MdEditSquare } from "react-icons/md";
import "react-datepicker/dist/react-datepicker.css";
import AdminLoader from "../../../../common/loader/AdminLoader";
import { MdSearch } from "react-icons/md";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  fetchClientRatingData,
  updateClientRatingData,
} from "../../../../../store/slices/clientRatingDashboardSliceL3";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";

import { useDispatch, useSelector } from "react-redux";
import { fetchLeads } from "../../../../../store/slices/leadsSlice";
import { fetchGroups } from "../../../../../store/slices/groupSlice";
import { fetchSavedClientRatingAll } from "./../../../../../store/slices/clientRatingDashboardSliceL3";
import { setCurrentPageTitle } from "../../../../../store/slices/pageSlice";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CommaConvert from "../../../../../utils/CommaConvert";
import { getFeatureAccessLevel } from "../../../../../utils/featureAccessUtils";

const ClientRatingDashboardL3 = () => {
  const dispatch = useDispatch();
  const USAComma = CommaConvert();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    dispatch(setCurrentPageTitle("Client Ratings"));
  }, [dispatch]);

  const [GroupNamesL3, setGroupNamesL3] = useState([]);
  const [LeadReports, setLeadReports] = useState([]);
  const [filteredLeadReports, setFilteredLeadReports] = useState([]);
  const [, setClientName] = useState([]);
  const [AssignedGroup, setAssignedGroup] = useState("All");
  /** Mongo Group _id for API (fetch/save); empty when filter is "All" */
  const [assignedGroupId, setAssignedGroupId] = useState("");
  const [AssignedClient, setAssignedClient] = useState("");
  /** Per-client group on the list: "" = average across that client's groups */
  const [selectedGroupByClient, setSelectedGroupByClient] = useState({});
  const [finalScale, setfinalScale] = useState("");
  const [, setTodaysDate] = useState("");
  const [showLogout, setshowlogout] = useState(false);
  const popupRef = useRef(null);
  const [, setIsDropdownOpen] = useState(false);
  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [isClientOpen, setIsClientOpen] = useState(false);
  const [totalfinalRating, settotalfinalRating] = useState(0);
  const [PercentageWithoutClient, setPercentageWithoutClient] = useState(0);
  const [PercentageWithClient, setPercentageWithClient] = useState(0);
  const [, settableData] = useState([]);
  const [historyWithoutClient, sethistoryWithoutClient] = useState(0);
  const [historyWithClient, sethistoryWithClient] = useState(0);
  const [ratingData, setRatingData] = useState([]);
  const [assignedPercentage, setAssignedPercentage] = useState(0);
  const [otherPercentage, setOtherPercentage] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [tempRatingName, setTempRatingName] = useState("");

  const { group } = useSelector((state) => state.group);
  const { leads } = useSelector((state) => state.leads);
  const { ratingDataAll } = useSelector((state) => state.clientRating);
  const { employees } = useSelector((state) => state.employees);
  const emailForAccess = localStorage.getItem("email");
  const currentEmployee = employees?.find((emp) => emp.EmpEmail === emailForAccess) ?? null;
  const hasNoFeatureConfig = !currentEmployee || !Array.isArray(currentEmployee?.featureAccess) || currentEmployee.featureAccess.length === 0;
  const clientRatingAccessLevel = getFeatureAccessLevel(currentEmployee, "CLIENT RATINGS CREATE CLIENT RATING");
  const isViewOnly = !!currentEmployee && !hasNoFeatureConfig && clientRatingAccessLevel && String(clientRatingAccessLevel).trim().toUpperCase() === "VIEW ONLY";

  const groupNameById = useMemo(() => {
    const map = new Map();
    const normalize = (v) => String(v ?? "").trim();
    (Array.isArray(group) ? group : []).forEach((g) => {
      const id = normalize(g?._id ?? g?.id);
      if (!id) return;
      const name = normalize(
        g?.name ??
        g?.newGroupName ??
        g?.groupName ??
        g?.GroupName ??
        id
      );
      map.set(id, name || id);
    });
    return map;
  }, [group]);

  const getLeadGroupId = useCallback((lead) => {
    const normalize = (value) => String(value ?? "").trim();
    const g = lead?.groupid ?? lead?.groupId;
    if (g != null && typeof g === "object") {
      const id = normalize(g._id ?? g.id ?? g.$oid);
      if (id) return id;
    }
    if (typeof lead?.groupid === "string") {
      const s = normalize(lead.groupid);
      if (s) return s;
    }
    if (typeof lead?.groupId === "string") {
      const s = normalize(lead.groupId);
      if (s) return s;
    }
    return "";
  }, []);

  const getLeadGroupName = useCallback(
    (lead) => {
      const gid = getLeadGroupId(lead);
      if (gid && groupNameById.has(gid)) return groupNameById.get(gid);
      const pop = lead?.groupid ?? lead?.groupId;
      if (pop && typeof pop === "object") {
        const n = String(
          pop.newGroupName ?? pop.name ?? pop.groupName ?? pop.GroupName ?? ""
        ).trim();
        if (n) return n;
      }
      return "";
    },
    [getLeadGroupId, groupNameById]
  );

  const getLeadRowGroupLabel = useCallback(
    (lead) => getLeadGroupName(lead),
    [getLeadGroupName]
  );

  const getGroupRowDisplayName = useCallback(
    (g) =>
      String(
        g?.newGroupName ??
          g?.name ??
          g?.groupName ??
          g?.GroupName ??
          ""
      ).trim() ||
      (g?._id || g?.id ? String(g._id ?? g.id) : ""),
    []
  );

  /** Prefer Mongo ObjectId timestamp when createdAt is missing */
  const getObjectIdTime = useCallback((id) => {
    const s = String(id ?? "").trim();
    if (/^[a-f\d]{24}$/i.test(s)) {
      return parseInt(s.substring(0, 8), 16) * 1000;
    }
    return Number.MAX_SAFE_INTEGER;
  }, []);

  const getGroupCreatedTime = useCallback(
    (groupId) => {
      const gid = String(groupId ?? "").trim();
      if (!gid) return Number.MAX_SAFE_INTEGER;
      const g = (Array.isArray(group) ? group : []).find(
        (x) => String(x._id ?? x.id ?? "").trim() === gid
      );
      if (g) {
        const d = g.createdAt || g.created_at || g.CreatedAt;
        if (d) {
          const t = new Date(d).getTime();
          if (!Number.isNaN(t)) return t;
        }
      }
      return getObjectIdTime(gid);
    },
    [group, getObjectIdTime]
  );

  const getLeadCreatedTime = useCallback(
    (lead) => {
      const d = lead?.createdAt || lead?.LeadDate || lead?.created_at;
      if (d) {
        const t = new Date(d).getTime();
        if (!Number.isNaN(t)) return t;
      }
      return getObjectIdTime(lead?._id);
    },
    [getObjectIdTime]
  );

  /** Exact client + group match only (no cross-group inheritance) */
  const findRatingForClientLead = useCallback(
    (lead) => {
      const clientName = lead?.ClientName;
      if (!clientName || !Array.isArray(ratingDataAll)) return null;

      const leadGid = getLeadGroupId(lead);
      return (
        ratingDataAll.find((rating) => {
          if (rating.clientName !== clientName) return false;
          const rid = String(rating.groupid?._id ?? rating.groupid ?? "").trim();
          if (leadGid && rid) return rid === leadGid;
          const groupLabel = getLeadGroupName(lead);
          return (
            !!groupLabel &&
            (rating.groupName === groupLabel ||
              rating.groupName === lead.AssignedGroup)
          );
        }) ?? null
      );
    },
    [ratingDataAll, getLeadGroupId, getLeadGroupName]
  );

  const getGroupsForClient = useCallback(
    (clientName) => {
      const target = String(clientName ?? "").trim();
      if (!target) return [];
      const seen = new Map();
      (Array.isArray(LeadReports) ? LeadReports : []).forEach((lead) => {
        if (String(lead.ClientName ?? "").trim() !== target) return;
        const gid = getLeadGroupId(lead);
        const name = getLeadGroupName(lead);
        if (!gid || seen.has(gid)) return;
        seen.set(gid, { gid, name: name || gid });
      });
      return [...seen.values()];
    },
    [LeadReports, getLeadGroupId, getLeadGroupName]
  );

  const getSavedRatingForClientGroup = useCallback(
    (clientName, groupId) => {
      if (!clientName || !groupId || !Array.isArray(ratingDataAll)) return null;
      const gid = String(groupId).trim();
      return (
        ratingDataAll.find((rating) => {
          if (rating.clientName !== clientName) return false;
          const rid = String(rating.groupid?._id ?? rating.groupid ?? "").trim();
          return rid === gid;
        }) ?? null
      );
    },
    [ratingDataAll]
  );

  const getAverageRatingForClient = useCallback(
    (clientName) => {
      const values = (Array.isArray(ratingDataAll) ? ratingDataAll : [])
        .filter((r) => r.clientName === clientName)
        .map((r) => Number(r.TotalRating))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (!values.length) return null;
      return values.reduce((sum, n) => sum + n, 0) / values.length;
    },
    [ratingDataAll]
  );

  const getRowRatingDisplay = useCallback(
    (lead) => {
      const clientName = lead?.ClientName;
      const groups = getGroupsForClient(clientName);
      const selectedGid =
        AssignedGroup === "All"
          ? selectedGroupByClient[clientName] ?? ""
          : "";
      const useAverage =
        AssignedGroup === "All" && groups.length > 1 && !selectedGid;

      if (useAverage) {
        const avg = getAverageRatingForClient(clientName);
        return avg ? Number(avg).toFixed(1) : " ";
      }

      const gid = selectedGid || getLeadGroupId(lead);
      const match = getSavedRatingForClientGroup(clientName, gid);
      const value = match?.TotalRating;
      return value ? Number(value).toFixed(1) : " ";
    },
    [
      AssignedGroup,
      selectedGroupByClient,
      getGroupsForClient,
      getLeadGroupId,
      getSavedRatingForClientGroup,
      getAverageRatingForClient,
    ]
  );

  const groupsForClientRating = useMemo(() => {
    const list = Array.isArray(GroupNamesL3) ? GroupNamesL3 : [];
    const raw = Array.isArray(currentEmployee?.EmpGroups)
      ? currentEmployee.EmpGroups
      : [];
    if (raw.length === 0) return list;
    const idSet = new Set(raw.map((x) => String(x)));
    return list.filter((g) => idSet.has(String(g._id ?? g.id ?? "")));
  }, [GroupNamesL3, currentEmployee?.EmpGroups]);

  useEffect(() => {
    dispatch(fetchLeads());
    dispatch(fetchGroups());
    dispatch(fetchSavedClientRatingAll());
  }, [dispatch]);

  useEffect(() => {
    if (group) {
      setGroupNamesL3(group);
    }
  }, [group]);

  useEffect(() => {
    const updateLeadReportsWithRatings = async () => {
      if (!leads || leads.length === 0) return;

      // Format client names
      const formattedClientNames = leads.map((lead, index) => ({
        id: index + 1,
        newClientName: lead.ClientName,
        _id: lead._id,
        __v: lead.__v || 0,
      }));
      setClientName(formattedClientNames);

      // Store raw leads
      // setLeadReports(leads);

      // Merge ratings if available (exact client + group only)
      const updatedReports = leads.map((lead) => {
        const match = findRatingForClientLead(lead);

        return {
          ...lead,
          Totalrating: match?.TotalRating ?? 0,
        };
      });

      setLeadReports(updatedReports);
    };

    updateLeadReportsWithRatings();
  }, [leads, ratingDataAll, group, findRatingForClientLead]);

  // useEffect(() => {
  //   const updateLeadReportsWithRatings = async () => {
  //     if (LeadReports.length === 0) return;

  //     // Wait until rating data is loaded
  //     if (ratingDataAll.length === 0) {
  //       setFilteredLeadReports(
  //         LeadReports.map((lead) => ({ ...lead, Totalrating: 0 }))
  //       );
  //       return;
  //     }

  //     // Merge rating into leads
  //     const updatedReports = LeadReports.map((lead) => {
  //       const match = ratingDataAll.find(
  //         (rating) =>
  //           rating.clientName === lead.ClientName &&
  //           rating.groupName === lead.AssignedGroup
  //       );

  //       return {
  //         ...lead,
  //         Totalrating: match?.TotalRating ?? 0,
  //       };
  //     });

  //     setFilteredLeadReports(updatedReports);
  //   };

  //   updateLeadReportsWithRatings();
  // }, [LeadReports, ratingDataAll]);

  // useEffect(() => {
  //   if (leads) {
  //     const filteredData = leads;
  //     const formattedClientNames = filteredData.map((lead, index) => ({
  //       id: index + 1, // Assigning a sequential ID
  //       newClientName: lead.ClientName, // Storing ClientName as newGroupName
  //       _id: lead._id, // Keeping original _id
  //       __v: lead.__v || 0, // Keeping version key, default to 0 if undefined
  //     }));

  //     setClientName(formattedClientNames);
  //     setLeadReports(filteredData);
  //     // setFilteredLeadReports(filteredData); // Initialize filtered list
  //   } else {
  //     // enqueueSnackbar('Failed to fetch Status', { variant: 'error' });
  //   }
  // }, [group, leads]);

  // for history====>
  useEffect(() => {
    const fetchStatus = async () => {
      const leads = LeadReports.filter(
        (lead) =>
          (assignedGroupId
            ? getLeadGroupId(lead) === assignedGroupId
            : getLeadRowGroupLabel(lead) === AssignedGroup) &&
          lead.ClientName === AssignedClient
      ) // Filter by group & client
        .map((lead) => new Date(lead.LeadDate));

      if (leads.length > 0) {
        const oldestLead = new Date(Math.min(...leads));
        const currentYear = new Date().getFullYear();
        const oldestLeadYear = oldestLead.getFullYear();

        const yearDifference = currentYear - oldestLeadYear;

        sethistoryWithClient(yearDifference);
      }

      const nonClientLeads = LeadReports.filter(
        (lead) => lead.ClientName !== AssignedClient
      ).map((lead) => new Date(lead.LeadDate).getFullYear());

      // ✅ Remove Duplicate Years
      const uniqueYears = [...new Set(nonClientLeads)];

      if (uniqueYears.length > 0) {
        const averageYear =
          uniqueYears.reduce((sum, year) => sum + year, 0) / uniqueYears.length;

        const roundedAvgYear = Math.round(averageYear);

        const avgYearDifference = new Date().getFullYear() - roundedAvgYear;

        sethistoryWithoutClient(avgYearDifference);
      }
    };

    fetchStatus();
  }, [
    AssignedClient,
    AssignedGroup,
    assignedGroupId,
    LeadReports,
    getLeadRowGroupLabel,
    getLeadGroupId,
  ]);

  // for won & won Rec average
  useEffect(() => {
    const fetchStatus = async () => {
      // Fetch data (assuming an API call)
      const response = LeadReports;

      // Get current date and subtract 5 years
      const fiveYearsAgo = new Date();
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);

      // Filter leads from the last 5 years
      const recentLeads = response.filter(
        (lead) => new Date(lead.LeadDate) >= fiveYearsAgo
      );

      // First Calculation: Leads under AssignedClient & AssignedGroup with "Won" or "Won & Won rec"
      const clientGroupLeads = recentLeads.filter(
        (lead) =>
          lead.ClientName === AssignedClient &&
          (assignedGroupId
            ? getLeadGroupId(lead) === assignedGroupId
            : getLeadRowGroupLabel(lead) === AssignedGroup) &&
          (lead.Status === "Won" || lead.Status === "Won Recurring")
      );

      // Second Calculation: Leads where ClientName is NOT AssignedClient
      const otherClientsLeads = recentLeads.filter(
        (lead) => lead.ClientName !== AssignedClient
      );

      // Calculate averages
      const avgQuotedClient = clientGroupLeads.length
        ? clientGroupLeads.reduce((sum, lead) => sum + lead.QuotedValue, 0) /
        clientGroupLeads.length
        : 0;

      const avgQuotedOthers = otherClientsLeads.length
        ? otherClientsLeads.reduce((sum, lead) => sum + lead.QuotedValue, 0) /
        otherClientsLeads.length
        : 0;
      setPercentageWithClient(avgQuotedClient);
      setPercentageWithoutClient(avgQuotedOthers);
    };

    fetchStatus();
  }, [
    AssignedClient,
    AssignedGroup,
    assignedGroupId,
    LeadReports,
    getLeadRowGroupLabel,
    getLeadGroupId,
  ]);

  // for won & won Rec %
  useEffect(() => {
    const calculatePercentages = () => {
      if (!LeadReports || LeadReports.length === 0) return;
      // Get date 5 years ago
      const fiveYearsAgo = new Date();
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);

      const totalLeadReport = LeadReports.filter(
        (lead) => new Date(lead.LeadDate).getTime() >= fiveYearsAgo.getTime()
      );

      // Filter leads from the last 5 years
      const TotalLeads = LeadReports.filter(
        (lead) =>
          (assignedGroupId
            ? getLeadGroupId(lead) === assignedGroupId
            : getLeadRowGroupLabel(lead) === AssignedGroup) &&
          lead.ClientName === AssignedClient &&
          new Date(lead.LeadDate) >= fiveYearsAgo // Ensure lead is within last 5 years
      );

      const wonAssignedLeads = TotalLeads.filter(
        (lead) => lead.Status === "Won" || lead.Status === "Won Recurring"
      );

      const otherClientsLeads = LeadReports.filter(
        (lead) => lead.ClientName !== AssignedClient
      );
      const percentageWithClient =
        (wonAssignedLeads.length / TotalLeads.length) * 100 || 0;
      const percentageOtherClient =
        (otherClientsLeads.length / totalLeadReport.length) * 100 || 0;
      setAssignedPercentage(percentageWithClient);
      setOtherPercentage(percentageOtherClient);
    };

    calculatePercentages();
  }, [
    AssignedClient,
    AssignedGroup,
    assignedGroupId,
    LeadReports,
    getLeadRowGroupLabel,
    getLeadGroupId,
  ]);

  useEffect(() => { }, [assignedPercentage, otherPercentage]);

  const [isLoading] = useState(false);

  const dropdownRef = useRef(null);
  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const groupdownRef = useRef(null);
  const handleClickOutsidegroup = (e) => {
    if (groupdownRef.current && !groupdownRef.current.contains(e.target)) {
      setIsGroupOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutsidegroup);
    return () => {
      document.removeEventListener("click", handleClickOutsidegroup);
    };
  }, []);

  const clientdownRef = useRef(null);

  const handleClickOutsideclient = (e) => {
    if (clientdownRef.current && !clientdownRef.current.contains(e.target)) {
      setIsClientOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutsideclient);
    return () => {
      document.removeEventListener("click", handleClickOutsideclient);
    };
  }, []);

  const handleClickOutsides = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setshowlogout(false); // Close the popup if clicked outside
    }
  };
  useEffect(() => {
    if (showLogout) {
      document.addEventListener("mousedown", handleClickOutsides);
    } else {
      document.removeEventListener("mousedown", handleClickOutsides);
    }
    return () => document.removeEventListener("mousedown", handleClickOutsides);
  }, [showLogout]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setTodaysDate(today);
  }, []);

  useEffect(() => { }, [AssignedGroup, AssignedClient]);

  useEffect(() => {
    let cancelled = false;

    const fetchRating = async () => {
      if (!AssignedClient?.trim() || !assignedGroupId) {
        setRatingData([]);
        setsortedData([]);
        return;
      }
      setRatingData([]);
      setsortedData([]);
      try {
        const response = await dispatch(
          fetchClientRatingData({
            AssignedClient,
            groupId: assignedGroupId,
          })
        ).unwrap();

        if (cancelled) return;
        if (Array.isArray(response)) {
          setRatingData(response);
        } else {
          setRatingData([]);
        }
      } catch (error) {
        console.error("Error fetching client rating data:", error);
        if (!cancelled) setRatingData([]);
      }
    };

    fetchRating();
    return () => {
      cancelled = true;
    };
  }, [assignedGroupId, AssignedClient, dispatch]);

  useEffect(() => { }, [ratingData]);

  const predefinedOrder = useMemo(
    () => ["ClientHistory", "Won & Won rec", "conversionRate"],
    []
  );

  const [sortedData, setsortedData] = useState([]);

  const sortedRatingData = useMemo(() => {
    return [...ratingData].sort((a, b) => {
      const indexA = predefinedOrder.indexOf(a.RatingName);
      const indexB = predefinedOrder.indexOf(b.RatingName);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB; // Sort by predefined order
      }
      if (indexA !== -1) return -1; // Move predefined items to the top
      if (indexB !== -1) return 1;

      return 0; // Keep remaining items in original order
    });
  }, [ratingData, predefinedOrder]);

  useEffect(() => {
    if (ratingData.length === 0) {
      setsortedData([]);
    } else {
      settableData(sortedRatingData);
      setsortedData(sortedRatingData);
    }
  }, [sortedRatingData, ratingData]);

  const makeDefaultRatings = useCallback(
    (clientName, groupIdStr) => {
      const gidOpt = groupIdStr ? { groupid: groupIdStr } : {};
      return [
        {
          RatingName: "ClientHistory",
          ScaleNo: 20,
          ScoreMark: "0",
          TotalRating: 0,
          clientName,
          ...gidOpt,
        },
        {
          RatingName: "Won & Won rec",
          ScaleNo: 20,
          ScoreMark: "0",
          TotalRating: 0,
          clientName,
          ...gidOpt,
        },
        {
          RatingName: "conversionRate",
          ScaleNo: 20,
          ScoreMark: "0",
          TotalRating: 0,
          clientName,
          ...gidOpt,
        },
        {
          RatingName: "Ease of Business",
          ScaleNo: 20,
          ScoreMark: "0",
          TotalRating: 0,
          clientName,
          ...gidOpt,
        },
        {
          RatingName: "Communication",
          ScaleNo: 20,
          ScoreMark: "0",
          TotalRating: 0,
          clientName,
          ...gidOpt,
        },
      ];
    },
    []
  );

  const defaultRatings = useMemo(
    () => makeDefaultRatings(AssignedClient, assignedGroupId),
    [makeDefaultRatings, AssignedClient, assignedGroupId]
  );

  const [ratings, setRatings] = useState(defaultRatings);

  useEffect(() => {
    if (!AssignedClient?.trim() || !assignedGroupId) return;
    if (sortedRatingData.length > 0) {
      setRatings(sortedRatingData);
    } else {
      setRatings(makeDefaultRatings(AssignedClient, assignedGroupId));
    }
  }, [sortedRatingData, AssignedClient, assignedGroupId, makeDefaultRatings]);

  // const handleChange = (index: number, field: string, value: string) => {
  //   // Allow only numbers and a single decimal point
  //   const numericValue = value.replace(/[^0-9]/g, "");

  //   // Prevent multiple decimal points
  //   const sanitizedValue =
  //     numericValue.split(".").length > 2
  //       ? numericValue.slice(0, -1)
  //       : numericValue;

  //   const updatedRatings = [...ratings];

  //   if (field === "ScoreMark") {
  //     const scaleValue = parseFloat(updatedRatings[index]["ScaleNo"]) || 0;
  //     const newValue = parseFloat(sanitizedValue) || 0;

  //     // Ensure ScoreMark does not exceed ScaleNo
  //     if (newValue > scaleValue) return;
  //   }

  //   updatedRatings[index][field] = sanitizedValue;

  //   setRatings(updatedRatings);
  // };
  const handleChange = (index, field, value) => {
    if (isViewOnly) return;
    // Allow digits and one decimal point
    const sanitizedValue = value
      .replace(/[^0-9.]/g, "")          // Remove non-numeric/non-dot
      .replace(/^\.*/, "")             // Remove leading dot(s)
      .replace(/(\..*)\./g, "$1");     // Allow only one dot

    // Prevent just a single dot
    if (sanitizedValue === ".") return;

    const updatedRatings = [...ratings];

    if (field === "ScoreMark") {
      const scaleValue = parseFloat(updatedRatings[index]["ScaleNo"]) || 0;
      const newValue = parseFloat(sanitizedValue) || 0;

      // Prevent exceeding ScaleNo
      if (newValue > scaleValue) return;
    }

    updatedRatings[index][field] = sanitizedValue;
    setRatings(updatedRatings);
  };


  const totalMainRating = ratings.reduce((sum, rating) => {
    const scaleNo = Number(rating.ScaleNo);
    const scoreMark = Number(rating.ScoreMark);
    const starConversion = scaleNo !== 0 ? (scaleNo / 100) * 5 : 0;
    const ScoreandScale = scaleNo !== 0 ? scoreMark / scaleNo : 0;
    const MainRating = ScoreandScale * starConversion;

    return sum + (isNaN(MainRating) ? 0 : MainRating); // Sum up all MainRatings
  }, 0);

  // const totalRows = ratings.length;
  // const finalTotalRating = totalRows > 0 ? totalMainRating / totalRows : 0;

  const totalScaleNo = ratings.reduce(
    (sum, rating) => sum + (Number(rating.ScaleNo) || 0), // Ensure no NaN
    0
  );

  useEffect(() => {
    settotalfinalRating(totalMainRating.toFixed(2));
  }, [totalMainRating]);
  useEffect(() => {
    // settotalfinalRating(isNaN(totalCalculatedRating) ? 0 : totalCalculatedRating);
    setfinalScale(isNaN(totalScaleNo) ? 0 : totalScaleNo);
  }, [totalScaleNo]);

  const handleEditClick = (index, currentName) => {
    if (isViewOnly) return;
    setEditingIndex(index);
    setTempRatingName(currentName);
  };

  const handleInputChange = (e) => {
    setTempRatingName(e.target.value);
  };
  const handleBlur = (index) => {
    const updatedRatings = [...ratings];
    updatedRatings[index].RatingName = tempRatingName;
    setRatings(updatedRatings);
    setEditingIndex(null);
  };
  const handleAddRow = () => {
    if (isViewOnly) return;
    setRatings((prevRatings) => {
      if (prevRatings.length >= 15) {
        enqueueSnackbar("You can add only up to 15 rows.", {
          variant: "error",
        });
        return prevRatings; // Prevent adding more rows
      }
      return [
        ...prevRatings,
        {
          RatingName: "New Rating",
          ScaleNo: 0,
          ScoreMark: "0",
          TotalRating: totalfinalRating,
          clientName: AssignedClient,
          ...(assignedGroupId ? { groupid: assignedGroupId } : {}),
        },
      ];
    });
  };

  useEffect(() => {
    setRatings((prevRatings) =>
      prevRatings.map((rating) => {
        const { groupName: _removed, ...rest } = rating;
        const gid = assignedGroupId || rest.groupid;
        return {
          ...rest,
          TotalRating: totalfinalRating,
          ...(gid ? { groupid: gid } : {}),
        };
      })
    );
  }, [totalfinalRating, assignedGroupId]);

  const handleRemoveRow = () => {
    if (isViewOnly) return;
    setRatings((prevRatings) => {
      if (prevRatings.length > 3) {
        return prevRatings.slice(0, -1);
      }
      enqueueSnackbar("You must keep at least 3 ratings!", {
        variant: "error",
      });
      return prevRatings;
    });
  };

  useEffect(() => { }, [ratings, defaultRatings]);
  const Submit = async (e) => {
    e.preventDefault();
    if (isViewOnly) return;

    const hasZeroScore = ratings.some((rating) => {
      const score = Number(rating.ScoreMark);
      return score === 0 || isNaN(score);
    });

    if (hasZeroScore) {
      enqueueSnackbar("All scores must be greater than 0!", {
        variant: "error"
      });
      return;
    }

    if (finalScale !== 100) {
      enqueueSnackbar("Scale must be 100!", { variant: "error" });
      return;
    }

    const ratingNamesSet = new Set();
    let hasDuplicate = ratings.some((rating) => {
      if (ratingNamesSet.has(rating.RatingName)) {
        return true;
      }
      ratingNamesSet.add(rating.RatingName);
      return false;
    });

    if (hasDuplicate) {
      enqueueSnackbar("Rating Names Should be Unique!", { variant: "error" });
      return;
    }

    const gid =
      assignedGroupId ||
      String(ratings[0]?.groupid?._id ?? ratings[0]?.groupid ?? "").trim();
    if (!gid) {
      enqueueSnackbar("Missing group for this client rating.", {
        variant: "error",
      });
      return;
    }

    const payload = ratings.map(({ groupName: _gn, ...row }) => ({
      ...row,
      groupid: gid,
    }));

    try {
      await dispatch(updateClientRatingData(payload)).unwrap();
      enqueueSnackbar("Rating Added Successfully!", { variant: "success" });
      setAssignedGroup("");
      setAssignedGroupId("");
      setAssignedClient("");
      setIsClientOpen(false);
      setClientTable(true);
      await dispatch(fetchSavedClientRatingAll());
      setAssignedGroup("All");
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Failed to add rating. Please try again.", {
        variant: "error",
      });
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [clientTable, setClientTable] = useState(true);
  const [searchHide, setSearchHide] = useState(true);

  const handleBack = () => {
    setIsClientOpen(false);
    setClientTable(true);
    setAssignedGroup("All");
    setAssignedGroupId("");
    setAssignedClient("");
    setRatingData([]);
    setsortedData([]);
    setRatings(makeDefaultRatings("", ""));
    setEditingIndex(null);
    setSearchHide(true);
  };

  const openClientRating = (lead) => {
    const clientName = lead.ClientName;
    const groups = getGroupsForClient(clientName);
    const selectedGid = selectedGroupByClient[clientName] || "";
    const useRowGroupDropdown = AssignedGroup === "All" && groups.length > 1;

    if (useRowGroupDropdown && !selectedGid) {
      enqueueSnackbar("Select a group to view or edit ratings.", {
        variant: "info",
      });
      return;
    }

    const gid =
      useRowGroupDropdown && selectedGid
        ? selectedGid
        : getLeadGroupId(lead);
    setAssignedClient(clientName);
    setAssignedGroupId(gid);
    setAssignedGroup(
      gid
        ? groupNameById.get(gid) ||
          groups.find((g) => g.gid === gid)?.name ||
          getLeadGroupName(lead) ||
          ""
        : getLeadRowGroupLabel(lead)
    );
    setRatingData([]);
    setsortedData([]);
    setRatings(makeDefaultRatings(clientName, gid));
    setEditingIndex(null);
    setIsClientOpen(true);
    setClientTable(false);
    setSearchHide(false);
  };

  const dropdownRefclient = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefclient.current &&
        !dropdownRefclient.current.contains(event.target)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [clientFilter, setClientFilter] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // useEffect(() => {
  //   if (!AssignedGroup || AssignedGroup === "") return;
  //   const uniqueClients = new Set();

  //   const filteredClients = LeadReports.filter((lead) => {
  //     const groupMatch =
  //       AssignedGroup === "All" || lead.AssignedGroup === AssignedGroup;
  //     const clientMatch =
  //       !clientFilter ||
  //       clientFilter.trim() === "" ||
  //       // lead.ClientName === clientFilter;
  //             lead.ClientName.toLowerCase() === clientFilter.toLowerCase();

  //     return groupMatch && clientMatch;
  //   })
  //     .sort((a, b) => a.ClientName.trim().localeCompare(b.ClientName.trim()))
  //     .filter((lead) => {
  //       const clientLower = lead.ClientName.toLowerCase();
  //       if (uniqueClients.has(clientLower)) return false;
  //       uniqueClients.add(clientLower);
  //       return true;
  //     });
  //   setFilteredLeadReports(filteredClients);
  // }, [LeadReports, AssignedGroup, clientFilter]);

  useEffect(() => {
    if (!AssignedGroup || AssignedGroup === "") return;

    const uniqueClients = new Set();

    const filteredClients = LeadReports.filter((lead) => {
      const groupMatch =
        AssignedGroup === "All" ||
        (assignedGroupId
          ? getLeadGroupId(lead) === assignedGroupId
          : getLeadRowGroupLabel(lead) === AssignedGroup);

      const clientMatch =
        !clientFilter ||
        clientFilter.trim() === "" ||
        lead.ClientName.toLowerCase() === clientFilter.toLowerCase();

      return groupMatch && clientMatch;
    })
      // Same client in multiple groups: keep the earliest-created group/client first
      .sort((a, b) => {
        const nameCmp = a.ClientName.trim().localeCompare(b.ClientName.trim());
        if (nameCmp !== 0) return nameCmp;
        const groupCmp =
          getGroupCreatedTime(getLeadGroupId(a)) -
          getGroupCreatedTime(getLeadGroupId(b));
        if (groupCmp !== 0) return groupCmp;
        return getLeadCreatedTime(a) - getLeadCreatedTime(b);
      })
      .filter((lead) => {
        const clientLower = lead.ClientName.toLowerCase();
        if (uniqueClients.has(clientLower)) return false;
        uniqueClients.add(clientLower);
        return true;
      });

    setFilteredLeadReports(filteredClients);
  }, [
    LeadReports,
    AssignedGroup,
    assignedGroupId,
    clientFilter,
    getLeadRowGroupLabel,
    getLeadGroupId,
    getGroupCreatedTime,
    getLeadCreatedTime,
  ]);


  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchInputChange = (e) => {
    const query = e.target.value; // Remove leading/trailing spaces
    setSearchQuery(query);
    setCurrentPage(1); // ✅ Reset to first page when searching
  };

  const searchedLeadReports = filteredLeadReports.filter((lead) => {
    const query = searchQuery.toLowerCase().trim();

    return (
      lead.ClientName.toLowerCase().includes(query) ||
      getLeadRowGroupLabel(lead).toLowerCase().includes(query) ||
      String(lead.Totalrating || "")
        .toLowerCase()
        .includes(query)
    );
  });

  useEffect(() => {
    if (!AssignedGroup) {
      setCurrentPage(1); // ✅ Reset to first page when filter
    }
  }, [AssignedGroup]);

  const getPaginatedClients = () => {
    const totalItems = searchedLeadReports.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const safePage = Math.min(currentPage, totalPages || 1); // clamp page number
    const startIdx = (safePage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    const paginatedClients = searchedLeadReports.slice(startIdx, endIdx);

    return { paginatedClients, totalItems, totalPages, startIdx, endIdx };
  };
  const sortAlphabetically = (targetToSort, order) => {
    const key = `${targetToSort}-${order}`;
    const sortedReports = [...filteredLeadReports].map((item) => ({ ...item }));

    switch (key) {
      case "business-az":
        sortedReports.sort((a, b) =>
          getLeadRowGroupLabel(a).localeCompare(getLeadRowGroupLabel(b))
        );
        break;
      case "business-za":
        sortedReports.sort((a, b) =>
          getLeadRowGroupLabel(b).localeCompare(getLeadRowGroupLabel(a))
        );
        break;
      case "client-az":
        sortedReports.sort((a, b) =>
          (a.ClientName ?? "").localeCompare(b.ClientName ?? "")
        );
        break;
      case "client-za":
        sortedReports.sort((a, b) =>
          (b.ClientName ?? "").localeCompare(a.ClientName ?? "")
        );
        break;
      default:
        console.error("Invalid Order");
    }

    setFilteredLeadReports(sortedReports);
  };

  const numericSort = (order) => {
    const sortedReports = [...filteredLeadReports];

    order === "az"
      ? sortedReports.sort(
        (a, b) => Number(a.Totalrating) - Number(b.Totalrating)
      )
      : sortedReports.sort(
        (a, b) => Number(b.Totalrating) - Number(a.Totalrating)
      );

    setFilteredLeadReports(sortedReports); // Update the state
  };

  const ResetSearch = (e) => {
    e.preventDefault();
    setSearchQuery("");
    setClientFilter("");
    setAssignedGroup("All");
    setAssignedGroupId("");
    setSelectedGroupByClient({});
  };
  return (
    <div className=" bg-[#f3f8f8] h-full w-full pt-2">
      {isLoading ? (
        <AdminLoader isLoading={isLoading} />
      ) : (
        <div className=" relative  scroll-visible justify-center top-0">
          <div className="flex-col p-">

            {!isClientOpen && (
              <div className="flex gap-1 justify-between bg-white  rounded-md p-1">
                <div className="w-full flex gap-3 p-1 justify-between align-middle items-center">
                  <div className="flex gap-3">
                    <div
                      className="relative flex gap-2 justify-center align-middle items-center"
                      ref={groupdownRef}
                    >
                      <label
                        htmlFor="selectedStatus"
                        className="block text-sm font-semibold text-gray-700 "
                      >
                        GROUP
                      </label>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsGroupOpen(!isGroupOpen);
                        }}
                        className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                      >
                        <span
                          onClick={() => setAssignedGroup()}
                          className="truncate min-w-0 flex-1 text-left"
                        >
                          {AssignedGroup ? AssignedGroup : "All"}
                        </span>
                        <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                      </button>
                      {isGroupOpen && (
                        <div className="absolute left-0 top-full w-full text-xs border overflow-hidden text-wrap border-gray-400 bg-white shadow-md rounded-md z-50">
                          <div
                            className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${AssignedGroup === "All" ? "bg-gray-300" : ""
                              }`}
                            onClick={() => {
                              setAssignedGroup("All"); // ✅ Set to All
                              setAssignedGroupId("");
                              setIsGroupOpen(false);
                              setRatings(makeDefaultRatings("", ""));
                              setAssignedClient("");
                              setClientTable(true);
                              setClientFilter("");
                            }}
                          >
                            <label>All</label>
                          </div>

                          {groupsForClientRating.map((group) => {
                            const gid = String(group._id ?? group.id ?? "");
                            const label = getGroupRowDisplayName(group);
                            return (
                              <div
                                key={gid || label}
                                className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${assignedGroupId === gid
                                  ? "bg-gray-300"
                                  : ""
                                  }`}
                                onClick={() => {
                                  setAssignedGroup(label);
                                  setAssignedGroupId(gid);
                                  setIsGroupOpen(false);
                                  setRatings(makeDefaultRatings("", gid));
                                  setAssignedClient("");
                                  setClientTable(true);
                                  setClientFilter("");
                                }}
                              >
                                <label>{label}</label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="relative flex align-middle items-center">
                      <label
                        htmlFor="selectedStatus"
                        className="block text-sm font-semibold text-gray-700 pr-2"
                      >
                        CLIENT
                      </label>
                      <div className="flex  p-0 border rounded-md align-middle items-center">
                        <ExpandMoreIcon
                          className="absolute right-1 cursor-pointer bg-white"
                          onClick={() =>
                            setShowClientDropdown(!showClientDropdown)
                          }
                        />

                        <button
                          type="button"
                          onClick={() =>
                            setShowClientDropdown(!showClientDropdown)
                          }
                          className="w-24 text-left text-sm outline-none p-2 text-nowrap truncate"
                        >
                          {clientFilter ? clientFilter : <p>Select</p>}
                        </button>
                      </div>

                      {showClientDropdown && (
                        <div
                          ref={dropdownRefclient}
                          style={{ zIndex: 9999 }}
                          className="absolute top-full left-0 w-full max-h-64 cursor-pointer overflow-y-auto bg-gray-100 text-sm border border-gray-300 shadow-lg"
                        >
                          <p
                            onClick={() => {
                              setClientFilter(""); // Clear filter
                              setShowClientDropdown(false);
                            }}
                            className="p-2 hover:bg-gray-200"
                          >
                            All
                          </p>

                          {[
                            ...new Set(
                              filteredLeadReports
                                .filter(
                                  (client) =>
                                    AssignedGroup === "All" ||
                                    (assignedGroupId
                                      ? getLeadGroupId(client) === assignedGroupId
                                      : getLeadRowGroupLabel(client) ===
                                        AssignedGroup)
                                )
                                .map((client) => client.ClientName)
                            ),
                          ]
                            .sort((a, b) => a.localeCompare(b))
                            .map((clientName, index) => (
                              <p
                                key={index}
                                onClick={() => {
                                  setClientFilter(clientName);
                                  setShowClientDropdown(false);
                                }}
                                className="p-2 hover:bg-gray-200"
                              >
                                {clientName}
                              </p>
                            ))}
                        </div>
                      )}
                    </div>

                    <div className=" w-16 flex align-middle justify-center">
                      <label
                        htmlFor="selectedStatus"
                        className="block text-gray-700"
                      ></label>
                      <button
                        type="button"
                        onClick={ResetSearch}
                          className="w-24 relative text-sm outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 "
                      >
                        Reset{" "}
                      </button>
                    </div>
                  </div>
                  {searchHide && (
                    <div className="flex justify-end items-center ">
                      <div className="flex gap-2 p-1 align-middle items-center bg-[#e0e7ff] rounded-full">
                        <MdSearch className=" w-5 h-5 text-gray-500" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={handleSearchInputChange}
                          placeholder="Search"
                          className="bg-[#e0e7ff] outline-none w-[80%]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* <div className="mt-2"> */}
            {clientTable &&
              (() => {
                const {
                  paginatedClients,
                  totalItems,
                  totalPages,
                  startIdx,
                  endIdx,
                } = getPaginatedClients();

                return (
                  <>
                    <table className="w-full divide-y divide-gray-200 mt-2 border text-xs">
                      <thead className="bg-[#e8e8e8] w-[100%] ">
                        <tr className="h-8">
                          <th className="px-4 py-1 text-start font-semibold capitalize tracking-wider w-[20%]">
                            Sr.No
                          </th>
                          <th className="px-4 py-1 text-start font-semibold capitalize tracking-wider w-[30%]">
                            <div className="flex items-center justify-between">
                              ClientName
                              <div className="flex flex-col gap-y-1">
                                <div
                                  className="cursor-pointer "
                                  onClick={() =>
                                    sortAlphabetically("client", "az")
                                  }
                                >
                                  <ArrowDropUp
                                    color="white"
                                    sx={{ fontSize: "22px" }}
                                  />
                                </div>

                                <div
                                  className="cursor-pointer -mt-2"
                                  onClick={() =>
                                    sortAlphabetically("client", "za")
                                  }
                                >
                                  <ArrowDropDown
                                    color="white"
                                    sx={{ fontSize: "22px" }}
                                  />
                                </div>
                              </div>
                            </div>
                          </th>
                          <th className="px-4 py-1 text-start font-semibold capitalize tracking-wider w-[30%]">
                            <div className="flex items-center justify-between">
                              GroupName
                              <div className="flex flex-col gap-y-1">
                                <div
                                  className="cursor-pointer "
                                  onClick={() =>
                                    sortAlphabetically("business", "az")
                                  }
                                >
                                  <ArrowDropUp
                                    color="white"
                                    sx={{ fontSize: "22px" }}
                                  />
                                </div>

                                <div
                                  className="cursor-pointer -mt-2"
                                  onClick={() =>
                                    sortAlphabetically("business", "za")
                                  }
                                >
                                  <ArrowDropDown
                                    color="white"
                                    sx={{ fontSize: "22px" }}
                                  />
                                </div>
                              </div>
                            </div>
                          </th>
                          <th className="px-4 py-1 text-start font-semibold capitalize tracking-wider w-[20%]">
                            <div className="flex items-center justify-between">
                              Rating
                              <div className="flex flex-col gap-y-1">
                                <div
                                  className="cursor-pointer "
                                  onClick={() => numericSort("za")}
                                >
                                  <ArrowDropUp
                                    color="white"
                                    sx={{ fontSize: "22px" }}
                                  />
                                </div>

                                <div
                                  className="cursor-pointer -mt-2"
                                  onClick={() => numericSort("az")}
                                >
                                  <ArrowDropDown
                                    color="white"
                                    sx={{ fontSize: "22px" }}
                                  />
                                </div>
                              </div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedClients.map((lead, index) => {
                          const clientGroups = getGroupsForClient(lead.ClientName);
                          const showGroupDropdown =
                            AssignedGroup === "All" && clientGroups.length > 1;
                          const selectedGid =
                            selectedGroupByClient[lead.ClientName] ?? "";

                          return (
                          <tr
                            title="Click to view ratings"
                            key={lead.id}
                            className="border text-md border-b  cursor-pointer hover:bg-gray-100"
                            onClick={() => openClientRating(lead)}
                          >
                            <td className="px-4 text-start border-b py-2 font-thin w-[20%]">
                              {startIdx + index + 1}
                            </td>
                            <td className="px-4 text-start border-b py-2 w-[30%]">
                              {lead.ClientName}
                            </td>
                            <td
                              className="px-4 text-start border-b py-2 w-[30%]"
                              onClick={
                                showGroupDropdown
                                  ? (e) => e.stopPropagation()
                                  : undefined
                              }
                            >
                              {showGroupDropdown ? (
                                <select
                                  value={selectedGid}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setSelectedGroupByClient((prev) => ({
                                      ...prev,
                                      [lead.ClientName]: e.target.value,
                                    }));
                                  }}
                                  className="w-full max-w-[11rem] outline-none border border-[#e0e7ff] rounded-md p-1 text-xs bg-white"
                                >
                                  <option value="">
                                    All groups ({clientGroups.length})
                                  </option>
                                  {clientGroups.map((g) => (
                                    <option key={g.gid} value={g.gid}>
                                      {g.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                getLeadGroupName(lead)
                              )}
                            </td>
                            <td className="px-4 text-start border-b py-2 w-[20%]">
                              {getRowRatingDisplay(lead)}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div className="pagination mt-2 flex justify-between items-center bg-white rounded-md p-1">
                      <div>
                        {totalItems > 0 ? (
                          <>
                            Showing {startIdx + 1} to{" "}
                            {Math.min(endIdx, totalItems)} of {totalItems}{" "}
                            Entries
                          </>
                        ) : (
                          <>Showing 0 Entries</>
                        )}
                      </div>

                      {totalPages > 1 && (
                        <div>
                          <button
                            onClick={() => setCurrentPage(1)}
                            className="px-2 py-2 mx-1 border rounded"
                            disabled={currentPage === 1}
                            title="First Page"
                          >
                            <KeyboardDoubleArrowLeftIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                            className="px-2 py-2 mx-2 border rounded"
                            disabled={currentPage === 1}
                          >
                            <ChevronRightIcon className="w-5 h-5 rotate-180" />

                            {/* Previous */}
                          </button>

                          {Array.from({ length: 3 }, (_, index) => {
                            const pageNumber = currentPage - 1 + index;
                            if (pageNumber >= 1 && pageNumber <= totalPages) {
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => setCurrentPage(pageNumber)}
                                  className={`px-4 py-2 mx-2 border rounded ${currentPage === pageNumber
                                    ? "bg-gray-300"
                                    : ""
                                    }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            }
                            return null;
                          })}

                          <button
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages)
                              )
                            }
                            className="px-2 py-2 mx-2 border rounded"
                            disabled={currentPage === totalPages}
                          >
                            <ChevronRightIcon className="w-5 h-5" />
                            {/* Next */}
                          </button>
                          <button
                            onClick={() => setCurrentPage(totalPages)}
                            className="px-2 py-2 mx-1 border rounded"
                            disabled={currentPage === totalPages}
                            title="Last Page"
                          >
                            <KeyboardDoubleArrowRightIcon className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

            {/* </div> */}

            <div>
              {isClientOpen && (
                <>
                  <div className="w-full flex justify-center py-5 bg-white rounded-md">
                    {AssignedClient && assignedGroupId && (
                      <div className="w-[5%] flex flex-col justify-end align-bottom items-end px-2">
                        <div
                          className={`p-2 rounded-md shadow-md border ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "bg-white cursor-pointer"}`}
                          onClick={handleAddRow}
                        >
                          <AddIcon className="w-5 h-5" />
                        </div>
                      </div>
                    )}
                    <div className="w-[90%] shadow-md rounded-md">

                      <div className="flex w-full bg-[#e8e8e8] rounded-t-md">
                        <div className="w-[5%]  flex justify-center align-middle items-center text-center p-1 py-3">
                          <p>Sr.no</p>
                        </div>
                        <div className="w-[45%] flex justify-center align-middle items-center text-center p-1 py-3">
                          <p>Rating Name</p>
                        </div>
                        <div className="w-[16.6%] flex justify-center align-middle items-center text-center p-1 py-3">
                          <p>Scale</p>
                        </div>
                        <div className="w-[16.6%] flex justify-center align-middle items-center text-center p-1 py-3">
                          <p>Score</p>
                        </div>
                        <div className="w-[16.8%] flex justify-center align-middle items-center text-center p-1 py-3">
                          <p>Rating</p>
                        </div>
                      </div>
                      {AssignedClient && assignedGroupId ? (
                        sortedData.length > 0 ? (
                          <>
                            <div>
                              {ratings.map((rating, index) => {
                                const starConversion =
                                  rating.ScaleNo && Number(rating.ScaleNo) !== 0
                                    ? Number(Number(rating.ScaleNo) / 100) * 5
                                    : 0;
                                const ScoreandScale =
                                  rating.ScaleNo && Number(rating.ScaleNo) !== 0
                                    ? Number(
                                      Number(rating.ScoreMark) /
                                      Number(rating.ScaleNo)
                                    )
                                    : 0;
                                const MainRating =
                                  ScoreandScale * starConversion;
                                // isNaN(calculatedRating) ? 0 : calculatedRating;

                                return (
                                  <>
                                    {/* <div className="">when rating available</div> */}

                                    <div key={index} className="row">
                                      <div className="flex w-full bg-white">
                                        <div className="w-[5%] border-b flex justify-center items-center text-center p-1">
                                          {index + 1}
                                        </div>
                                        <div className="w-[45%] border-b flex  items-center  p-2">
                                          {rating.RatingName ===
                                            "ClientHistory" && (
                                              <div className="text-left">
                                                <p>
                                                  {" "}
                                                  History ={" "}
                                                  {historyWithClient === 0
                                                    ? 1
                                                    : historyWithClient}{" "}
                                                  Years
                                                </p>
                                                <p>
                                                  {" "}
                                                  All Other Client/industry
                                                  Average = {
                                                    historyWithoutClient
                                                  }{" "}
                                                  Years
                                                </p>
                                              </div>
                                            )}
                                          {rating.RatingName ===
                                            "Won & Won rec" && (
                                              <div className="text-left">
                                                <p>
                                                  {" "}
                                                  Won & Won Rec =
                                                  $
                                                  {USAComma(PercentageWithClient)}
                                                  Per Year
                                                </p>
                                                <p>
                                                  {" "}
                                                  All Other Client/industry
                                                  Average = $
                                                  {USAComma(
                                                    PercentageWithoutClient
                                                  )}{" "}
                                                  Per Year
                                                </p>
                                              </div>
                                            )}
                                          {rating.RatingName ===
                                            "conversionRate" && (
                                              <div className="text-left">
                                                <p>
                                                  {" "}
                                                  Conversion Rate
                                                  ={" "}
                                                  {assignedPercentage.toFixed(0)}{" "}
                                                  % Per Year
                                                </p>
                                                <p>
                                                  {" "}
                                                  All Other Client/industry
                                                  Average ={" "}
                                                  {otherPercentage > 100
                                                    ? "100"
                                                    : otherPercentage.toFixed(
                                                      0
                                                    )}{" "}
                                                  % Per Year
                                                </p>
                                              </div>
                                            )}
                                          {![
                                            "ClientHistory",
                                            "Won & Won rec",
                                            "conversionRate",
                                          ].includes(rating.RatingName) && (
                                              <div className=" relative  w-full">
                                                {editingIndex === index && !isViewOnly ? (
                                                  <input
                                                    type="text"
                                                    value={tempRatingName}
                                                    maxLength={30}
                                                    onChange={handleInputChange}
                                                    onBlur={() =>
                                                      handleBlur(index)
                                                    }
                                                    autoFocus
                                                    className="bg-transparent border-b border-none  border-white outline-none w-full"
                                                  />
                                                ) : (
                                                  <div className="flex w-full">
                                                    <p className="text-center">
                                                      {rating.RatingName}
                                                    </p>
                                                    <MdEditSquare
                                                      className={`absolute right-1 w-5 h-5 ${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer"}`}
                                                      onClick={() =>
                                                        handleEditClick(
                                                          index,
                                                          rating.RatingName
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                        </div>
                                        <div className="w-[16.6%] border-b flex justify-center items-center text-center p-1">
                                          <input
                                            type="text"
                                            disabled={isViewOnly}
                                            className={`h-full w-full border-none text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-transparent"}`}
                                            value={rating.ScaleNo}
                                            maxLength={2}
                                            onChange={(e) => {
                                              if (isViewOnly) return;
                                              if (e.target.value.length > 2)
                                                return;
                                              handleChange(
                                                index,
                                                "ScaleNo",
                                                e.target.value
                                              );
                                            }}
                                          />
                                        </div>
                                        <div className="w-[16.6%] border-b flex justify-center items-center text-center p-1">
                                          <input
                                            type="text"
                                            disabled={isViewOnly}
                                            className={`h-full w-full border-none text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-transparent"}`}
                                            value={rating.ScoreMark}
                                            maxLength={2}
                                            onChange={(e) => {
                                              if (isViewOnly) return;
                                              let value = e.target.value;
                                              if (
                                                value.startsWith("0") &&
                                                value.length > 1
                                              ) {
                                                value = value.slice(1);
                                              }
                                              handleChange(
                                                index,
                                                "ScoreMark",
                                                value
                                              );
                                            }}
                                          />
                                        </div>
                                        <div className="w-[16.8%] border-b flex justify-center items-center text-center p-1">
                                          <p>{MainRating.toFixed(2)}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                );
                              })}
                            </div>
                            <div>
                              <div className="flex w-full bg-white">
                                <div className="w-[50%] border-b flex justify-center items-center text-center p-2">
                                  <div className="text-left">
                                    <p>Total</p>
                                  </div>
                                </div>
                                <div className="w-[16.6%] border-b flex justify-center items-center text-center p-1">
                                  100
                                  {/* {totalScaleNo} */}
                                </div>
                                <div className="w-[16.6%] border-b flex justify-center items-center text-center p-1"></div>
                                <div className="w-[16.8%] border-b flex justify-center items-center text-center p-1">
                                  <p>
                                    {totalfinalRating === "NAN"
                                      ? 0
                                      : totalfinalRating}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="flex w-full bg-white">
                                <div className="w-[50%] border-b flex justify-center items-center text-center p-2">
                                  <div className="text-left"></div>
                                </div>
                                <div className="w-[16.6%] flex justify-center border-b items-center text-center text-red-600 text-sm p-1">
                                  {totalScaleNo < 100 || totalScaleNo > 100
                                    ? "Scale must be 100"
                                    : ""}
                                </div>
                                <div className="w-[16.6%] flex justify-center border-b items-center text-center p-1"></div>
                                <div className="w-[16.8%] flex justify-center border-b items-center text-center p-1"></div>
                              </div>
                            </div>

                            {/* <div className="flex justify-between p-1">
                          <div className="bg-white p-2 rounded-md" onClick={handleAddRow}>
                            <AddIcon className="w-5 h-5 cursor-pointer" />
                          </div>
                          <div className="bg-white p-2 rounded-md" onClick={handleRemoveRow}>
                            <RemoveIcon className="w-5 h-5 cursor-pointer" />
                          </div>
                        </div> */}
                          </>
                        ) : (
                          <>
                            {ratings.map((rating, index) => {
                              const starConversion =
                                rating.ScaleNo && Number(rating.ScaleNo) !== 0
                                  ? Number(Number(rating.ScaleNo) / 100) * 5
                                  : 0;
                              const ScoreandScale =
                                rating.ScaleNo && Number(rating.ScaleNo) !== 0
                                  ? Number(
                                    Number(rating.ScoreMark) /
                                    Number(rating.ScaleNo)
                                  )
                                  : 0;
                              const MainRating = ScoreandScale * starConversion;
                              return (
                                <>
                                  {/* <div className="">when rating not available</div> */}
                                  <div key={index} className="row">
                                    <div className="flex w-full bg-white">
                                      <div className="w-[5%] border-b flex justify-center items-center text-center p-1">
                                        {index + 1}
                                      </div>
                                      <div className="w-[45%] border-b flex  items-center  p-2">
                                        {rating.RatingName ===
                                          "ClientHistory" && (
                                            <div className="text-left">
                                              <p>
                                                {" "}
                                                {/* {AssignedClient}  */}
                                                History ={" "}
                                                {historyWithClient === 0
                                                  ? "1"
                                                  : historyWithClient}{" "}
                                                Years
                                              </p>
                                              <p>
                                                {" "}
                                                All Other Client/industry Average
                                                = {historyWithoutClient} Years
                                              </p>
                                            </div>
                                          )}
                                        {rating.RatingName ===
                                          "Won & Won rec" && (
                                            <div className="text-left">
                                              <p>
                                                {/* {AssignedClient}  */}
                                                Won & Won Rec = $
                                                {USAComma(
                                                  PercentageWithClient.toFixed(0)
                                                )}{" "}
                                                Per Year
                                              </p>
                                              <p>
                                                All Other Client/industry Average
                                                = $
                                                {USAComma(
                                                  PercentageWithoutClient.toFixed(
                                                    0
                                                  )
                                                )}{" "}
                                                Per Year
                                              </p>
                                            </div>
                                          )}
                                        {rating.RatingName ===
                                          "conversionRate" && (
                                            <div className="text-left">
                                              <p>
                                                {" "}
                                                {/* {AssignedClient}  */}
                                                Conversion Rate ={" "}
                                                {assignedPercentage.toFixed(0)} %
                                                Per Year
                                              </p>
                                              <p>
                                                {" "}
                                                All Other Client/industry Average
                                                ={" "}
                                                {otherPercentage > 100
                                                  ? "100"
                                                  : otherPercentage.toFixed(
                                                    0
                                                  )}{" "}
                                                % Per Year
                                              </p>
                                            </div>
                                          )}
                                        {![
                                          "ClientHistory",
                                          "Won & Won rec",
                                          "conversionRate",
                                        ].includes(rating.RatingName) && (
                                            <div className=" relative  w-full">
                                              {editingIndex === index && !isViewOnly ? (
                                                <input
                                                  type="text"
                                                  value={tempRatingName}
                                                  maxLength={30}
                                                  onChange={handleInputChange}
                                                  onBlur={() => handleBlur(index)}
                                                  autoFocus
                                                  className="bg-transparent border-b border-none  border-white outline-none w-full"
                                                />
                                              ) : (
                                                <div className="flex w-full">
                                                  <p className="text-center">
                                                    {rating.RatingName}
                                                  </p>
                                                  <MdEditSquare
                                                    className={`absolute right-1 w-5 h-5 ${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer"}`}
                                                    onClick={() =>
                                                      handleEditClick(
                                                        index,
                                                        rating.RatingName
                                                      )
                                                    }
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          )}
                                      </div>
                                      <div className="w-[16.6%] border-b flex justify-center items-center text-center p-1">
                                        <input
                                          type="text"
                                          disabled={isViewOnly}
                                          className={`h-full w-full border-none text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-transparent"}`}
                                          maxLength={2}
                                          value={rating.ScaleNo}
                                          onChange={(e) => {
                                            if (isViewOnly) return;
                                            if (e.target.value.length > 2)
                                              return;
                                            handleChange(
                                              index,
                                              "ScaleNo",
                                              e.target.value
                                            );
                                          }}
                                        />
                                      </div>
                                      <div className="w-[16.6%] border-b flex justify-center items-center text-center p-1">
                                        <input
                                          type="text"
                                          disabled={isViewOnly}
                                          className={`h-full w-full border-none text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-transparent"}`}
                                          value={rating.ScoreMark}
                                          maxLength={2}
                                          onChange={(e) => {
                                            if (isViewOnly) return;
                                            let value = e.target.value;
                                            if (
                                              value.startsWith("0") &&
                                              value.length > 1
                                            ) {
                                              value = value.slice(1);
                                            }
                                            handleChange(
                                              index,
                                              "ScoreMark",
                                              value
                                            );
                                          }}
                                        />
                                      </div>
                                      <div className="w-[16.8%]  border-b flex justify-center items-center text-center p-1">
                                        <p>{MainRating.toFixed(2)}</p>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              );
                            })}
                            <div>
                              <div className="flex w-full bg-white">
                                <div className="w-[50%]  border-b flex justify-center items-center text-center p-2">
                                  <div className="text-left">
                                    <p>Total</p>
                                  </div>
                                </div>
                                <div className="w-[16.6%]  border-b flex justify-center items-center text-center p-1">
                                  100
                                  {/* {totalScaleNo} */}
                                </div>
                                <div className="w-[16.6%]  border-b flex justify-center items-center text-center p-1"></div>
                                <div className="w-[16.8%]  border-b flex justify-center items-center text-center p-1">
                                  <p>
                                    {totalfinalRating === "NAN"
                                      ? 0
                                      : totalfinalRating}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="flex w-full bg-white">
                                <div className="w-[50%]  flex justify-center items-center text-center p-2">
                                  <div className="text-left"></div>
                                </div>
                                <div className="w-[16.6%]   flex justify-center items-center text-center text-red-600 text-sm p-1">
                                  {totalScaleNo < 100 || totalScaleNo > 100
                                    ? "Scale must be 100"
                                    : ""}
                                </div>
                                <div className="w-[16.6%]   flex justify-center items-center text-center p-1"></div>
                                <div className="w-[16.8%]   flex justify-center items-center text-center p-1"></div>
                              </div>
                            </div>
                            {/* <div className="flex justify-end py-3">
                          <button
                            onClick={Submit}
                            className=" py-2 px-7 bg-[#e32278] overflow-hidden shadow-md text-white rounded-lg"
                          >
                            Submit
                          </button>
                        </div> */}
                          </>
                        )
                      ) : (
                        <p className="text-red-600 bg-white p-1">No data</p>
                      )}
                    </div>
                    {AssignedClient && assignedGroupId && (
                      <div className="w-[5%] flex flex-col justify-end align-bottom items-start px-2">
                        <div
                          className={`p-2 rounded-md shadow-md border ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "bg-white cursor-pointer"}`}
                          onClick={handleRemoveRow}
                        >
                          <RemoveIcon className="w-5 h-5" />
                        </div>
                      </div>
                    )}
                  </div>
                  {AssignedClient && assignedGroupId && (
                    <div className="flex justify-between align-middle items-center py-3 px-14">
                      <div className="flex justify-start py-3 ">
                        <button
                          onClick={handleBack}
                          className=" py-2 px-7 bg-[#e32278] overflow-hidden shadow-md text-white rounded-lg"
                        >
                          Back
                        </button>
                      </div>
                      <button
                        onClick={Submit}
                        disabled={isViewOnly}
                        className={`py-2 px-7 overflow-hidden shadow-md rounded-lg ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-[#e32278] text-white"}`}
                      >
                        Submit
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRatingDashboardL3;
